import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Mail,
  Shield,
  ShieldOff,
  UserCheck,
  UserPlus,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ModalOverlay } from "@/components/ModalOverlay";
import { PaginatedList } from "@/components/PaginatedList";
import { PartnerPicker } from "@/components/PartnerPicker";
import { PosAccessPanel } from "@/components/PosAccessPanel";
import { Toggle } from "@/components/Toggle";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePaginatedList } from "@/lib/pagination";
import { canImpersonateTarget, hasWriteOn, isSuperAdmin, rolesForScope } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import { validateEmail } from "@/lib/validation";
import type {
  Partner,
  User,
  UserMembership,
  UserMembershipScopeKind,
  UserRole,
} from "@tilt/admin-sdk";

const ROLE_OPTIONS: UserRole[] = [
  "super_admin",
  "partner_admin",
  "partner_viewer",
  "organization_admin",
  "location_manager",
];

const ROLE_LABEL: Record<UserRole, string> = {
  super_admin: "Super admin",
  partner_admin: "Partner admin",
  partner_viewer: "Partner viewer",
  organization_admin: "Organization admin",
  location_manager: "Location manager",
};

type StatusFilter = "all" | "active" | "disabled";

export function UsersPage(): JSX.Element {
  usePageTitle("Users");
  const queryClient = useQueryClient();
  const { scope } = usePartnerScope();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [roleFilter, setRoleFilter] = useState<UserRole | "all">("all");

  // Users list paginates server-side. Status + role filters stay as
  // client-side refinements on the current page only - users wanting
  // to find a single disabled admin in a big directory should fall
  // back to the search box, which IS server-side. Adding backend
  // status/role filters is a follow-up (needs a JOIN on
  // user_membership_tbl.role or a COALESCE on the membership agg).
  const usersList = usePaginatedList({
    queryKey: ["users", scope.id],
    fetcher: ({ limit, offset, q }) =>
      apiClient
        .listUsers({ limit, offset, ...(q ? { q } : {}), scope_path: scope.path })
        .then((res) => ({
          items: res.users,
          total: res.total,
          limit: res.limit,
          offset: res.offset,
          has_more: res.has_more,
        })),
  });

  const onUserMutated = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  // "Invite user" surfaces for anyone holding a write role at any
  // tier - super_admin / partner_admin / organization_admin /
  // location_manager. The dialog itself constrains the new user's
  // initial_role to the caller's grant scope, so a location_manager
  // can only mint location_managers at their own location.
  const profile = useRoleProfile();
  const _WRITE_ROLES_FRONTEND: ReadonlySet<UserRole> = new Set([
    "partner_admin",
    "organization_admin",
    "location_manager",
  ]);
  const canInvite =
    isSuperAdmin(profile) || (profile?.roles ?? []).some((r) => _WRITE_ROLES_FRONTEND.has(r.role));

  // Partners list is shared across UserRows - each row needs the
  // target user's parent partner (path-slice match) for the
  // impersonation flag check. Query observer lives here so 20 rows
  // don't mount 20 copies of the same query. Key + fetch stay in
  // lockstep with the Sidebar's gate query so the react-query cache
  // dedupes the request across the two components.
  const partnersQuery = useQuery({
    queryKey: ["partners", "sidebar-gates", profile?.primary_partner?.id ?? null],
    queryFn: () => apiClient.listPartners({ limit: 100 }),
    staleTime: 5 * 60_000,
    enabled: !!profile,
  });
  const partners = partnersQuery.data?.partners ?? [];

  const visibleUsers = useMemo(() => {
    return usersList.items.filter((u) => {
      if (statusFilter === "active" && u.is_disabled) return false;
      if (statusFilter === "disabled" && !u.is_disabled) return false;
      if (roleFilter !== "all" && !u.roles.some((r) => r.role === roleFilter)) {
        return false;
      }
      return true;
    });
  }, [usersList.items, statusFilter, roleFilter]);

  // List of every active user_id holding super_admin in the current
  // page. Used by UserRow to hide the disable toggle when the row IS
  // the only super (locking the platform out is a critical-incident
  // motion the server also rejects). Heuristic on the visible page -
  // close enough for the small platform-admin team.
  const visibleSuperAdminUserIds = useMemo(
    () =>
      usersList.items
        .filter((u) => !u.is_disabled && u.roles.some((r) => r.role === "super_admin"))
        .map((u) => u.id),
    [usersList.items],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Users</h1>
          <p className="text-sm text-muted-foreground">
            People with access to this admin console. Invite new admins and manage their roles.
          </p>
        </div>
        {canInvite && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <UserPlus className="h-4 w-4" />
            Invite user
          </button>
        )}
      </div>

      <PaginatedList
        state={usersList}
        itemNoun="users"
        searchPlaceholder="Search by name, email, or partner…"
        filters={
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Status
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="all">All</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Role
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as UserRole | "all")}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="all">All roles</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
        emptyState={
          <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            No users in scope yet. Invite one to get started.
          </div>
        }
      >
        {usersList.query.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {usersList.query.error instanceof Error
              ? usersList.query.error.message
              : "Failed to load users"}
          </div>
        )}
        {visibleUsers.length > 0 && (
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 w-8" aria-label="Expand" />
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Primary scope</th>
                  <th className="px-4 py-2.5 text-right">Roles</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    partners={partners}
                    visibleSuperAdminUserIds={visibleSuperAdminUserIds}
                    onMutated={onUserMutated}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {usersList.items.length > 0 && visibleUsers.length === 0 && (
          <p className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-xs text-muted-foreground">
            The status/role filters hid every row on this page. Clear them or paginate to see other
            rows.
          </p>
        )}
      </PaginatedList>

      {inviteOpen && (
        <InviteDialog onClose={() => setInviteOpen(false)} onMutated={onUserMutated} />
      )}
    </div>
  );
}

function UserRow(props: {
  user: User;
  /**
   * Full partners list in the caller's scope - passed in by
   * ``UsersPage`` so every row reuses the same query observer
   * instead of mounting its own copy. Used to resolve each user's
   * parent partner for the impersonation flag check.
   */
  partners: ReadonlyArray<Pick<Partner, "id" | "path" | "can_impersonate">>;
  /** User ids of every active super_admin in the visible list - used
   * to gate the disable toggle when this row IS the only super. */
  visibleSuperAdminUserIds: ReadonlyArray<string>;
  onMutated: () => void;
}): JSX.Element {
  const { user, partners, visibleSuperAdminUserIds, onMutated } = props;
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const profile = useRoleProfile();
  const isSuper = isSuperAdmin(profile);
  // Enable/disable user (Cognito-level) is super_admin only - it
  // locks the user out platform-wide. Non-super callers get the
  // partner-scoped "Remove from hierarchy" action instead, which
  // strips memberships inside their write subtree but leaves the
  // Cognito record alone.
  // Self-disable + last-super-disable are server-rejected (400);
  // also hide the toggle here so the option doesn't tease an action
  // the backend will refuse. Self detection uses the loaded profile;
  // last-super detection counts the super_admin grants in the visible
  // user list - close-enough heuristic given that the platform's
  // super_admin team is small and Users page lists every user the
  // caller can read. The server is the source of truth either way.
  const isSelf = profile?.user_id === user.id;
  const isOnlySuperAdmin =
    user.roles.some((r) => r.role === "super_admin") &&
    visibleSuperAdminUserIds.length === 1 &&
    visibleSuperAdminUserIds[0] === user.id;
  const canCognitoToggle =
    isSuper && hasWriteOn(profile, user.primary_partner.path) && !isSelf && !isOnlySuperAdmin;
  const canRemoveFromHierarchy =
    !isSuper && hasWriteOn(profile, user.primary_partner.path) && !isSelf;

  // Impersonation uses the revised role matrix: super bypasses;
  // partner_admin needs target's parent partner to have
  // can_impersonate=TRUE AND caller to have write on that parent.
  // Parent partner is looked up from the ``partners`` prop by
  // path-slice - strip the last label off target.primary_partner.path
  // and match against the list.
  const parentPath = user.primary_partner.path.split(".").slice(0, -1).join(".");
  const parentPartner =
    parentPath.length > 0 ? partners.find((p) => p.path === parentPath) : undefined;
  const canImpersonate = canImpersonateTarget(profile, parentPartner);

  const toggle = useMutation({
    mutationFn: () =>
      user.is_disabled ? apiClient.enableUser(user.id) : apiClient.disableUser(user.id),
    onSuccess: onMutated,
  });
  const removeFromHierarchy = useMutation({
    mutationFn: () => apiClient.removeUserFromHierarchy(user.id),
    onSuccess: () => {
      setConfirmRemove(false);
      onMutated();
    },
  });
  const [resendFeedback, setResendFeedback] = useState<string | null>(null);
  // Hold the auto-clear timer on a ref so a mid-flight unmount
  // (pagination flip, filter change, route transition) cancels it
  // instead of firing setState against a dead component.
  const resendFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (resendFeedbackTimer.current) clearTimeout(resendFeedbackTimer.current);
    },
    [],
  );
  const resendInvite = useMutation({
    mutationFn: () => apiClient.resendUserInvite(user.id),
    onSuccess: () => {
      setResendFeedback("Invite email re-sent.");
      if (resendFeedbackTimer.current) clearTimeout(resendFeedbackTimer.current);
      resendFeedbackTimer.current = setTimeout(() => setResendFeedback(null), 4000);
      onMutated();
    },
    onError: (err: unknown) => setResendFeedback(describe(err)),
  });

  // Invite-pending = never signed in and not disabled. Cognito keeps
  // the user in FORCE_CHANGE_PASSWORD until they accept the invite +
  // set a password; the first sign-in stamps last_login_at on the
  // user_tbl row, which is the signal we read here (no extra Cognito
  // round-trip). Once they sign in, status flips to Active.
  const isPending = !user.is_disabled && !user.last_login_at;
  const canResendInvite = isPending && hasWriteOn(profile, user.primary_partner.path);

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email.split("@")[0];

  const rolesSummary =
    user.roles.length === 0
      ? "No roles"
      : user.roles.length === 1
        ? ROLE_LABEL[user.roles[0]!.role]
        : `${user.roles.length} roles`;

  return (
    <>
      <tr className="border-b last:border-b-0 hover:bg-muted/30">
        <td className="px-2 py-2.5 align-middle">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls={`user-roles-${user.id}`}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
            title={expanded ? "Collapse" : "Expand roles"}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="px-4 py-2.5 align-middle">
          <span className="font-medium">{displayName}</span>
        </td>
        <td className="px-4 py-2.5 align-middle text-muted-foreground">{user.email}</td>
        <td className="px-4 py-2.5 align-middle font-mono text-xs text-muted-foreground">
          {user.primary_partner.path}
        </td>
        <td className="px-4 py-2.5 text-right align-middle text-xs text-muted-foreground">
          {rolesSummary}
        </td>
        <td className="px-4 py-2.5 align-middle">
          {user.is_disabled ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
              <Ban className="h-3 w-3" />
              Disabled
            </span>
          ) : isPending ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
              <Mail className="h-3 w-3" />
              Invited
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              <CheckCircle2 className="h-3 w-3" />
              Active
            </span>
          )}
        </td>
        <td className="px-4 py-2.5 align-middle">
          <div className="flex flex-wrap justify-end gap-1.5">
            {canImpersonate && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/impersonate?target=${encodeURIComponent(user.id)}`, {
                    state: { target: user },
                  })
                }
                title="Impersonate"
                className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
              >
                <UserCheck className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Impersonate</span>
              </button>
            )}
            {canResendInvite && (
              <>
                <button
                  type="button"
                  onClick={() => resendInvite.mutate()}
                  disabled={resendInvite.isPending}
                  title={resendFeedback ?? "Re-send the Cognito invite email"}
                  aria-label={
                    resendInvite.isPending ? "Resending invite email" : "Resend invite email"
                  }
                  className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                >
                  <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="hidden sm:inline">
                    {resendInvite.isPending ? "Sending…" : "Resend invite"}
                  </span>
                </button>
                {/* Live region so screen readers announce success /
                    error without relying on title text. Visually
                    hidden; the inline button text already covers the
                    happy case on wider screens. */}
                <span className="sr-only" aria-live="polite">
                  {resendFeedback ?? ""}
                </span>
              </>
            )}
            {canCognitoToggle && (
              <Toggle
                size="sm"
                checked={!user.is_disabled}
                disabled={toggle.isPending}
                onChange={() => toggle.mutate()}
                ariaLabel={user.is_disabled ? "Enable user" : "Disable user"}
              />
            )}
            {canRemoveFromHierarchy && !user.is_disabled && (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                title="Remove from your hierarchy (Cognito account untouched)"
                className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-destructive/10 hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Remove</span>
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr id={`user-memberships-${user.id}`} className="border-b last:border-b-0 bg-muted/20">
          <td colSpan={7} className="px-4 py-3">
            <div className="space-y-3">
              <MembershipsSection userId={user.id} />
              {/* POS access - separate from admin memberships per V0042
                  (different population, different Cognito pool). Same
                  email correlates the two; ``pos_user_tbl`` rows are
                  shown here as a convenience but the API treats them
                  as their own resource. */}
              <PosAccessPanel userEmail={user.email} />
            </div>
          </td>
        </tr>
      )}
      {confirmRemove && (
        <ModalOverlay
          onClose={() => setConfirmRemove(false)}
          ariaLabel="Confirm remove user from hierarchy"
        >
          <div className="w-[460px] max-w-full rounded-lg border bg-card p-5 shadow-lg">
            <h3 className="text-sm font-semibold">Remove user from your hierarchy?</h3>
            <p className="mt-2 text-xs text-muted-foreground">
              Strips every role membership <span className="font-mono">{user.email}</span> holds
              inside partners you administer. The user's account is NOT disabled - they may still
              belong to other partner trees you can't see. This cannot be undone from here
              (re-invite to restore access).
            </p>
            {removeFromHierarchy.isError && (
              <p className="mt-2 text-xs text-destructive">{describe(removeFromHierarchy.error)}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmRemove(false)}
                className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => removeFromHierarchy.mutate()}
                disabled={removeFromHierarchy.isPending}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs text-destructive-foreground hover:opacity-90 disabled:opacity-50"
              >
                {removeFromHierarchy.isPending ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

/**
 * V0024 - Memberships section inside the expanded user row.
 * Replaces the old Roles list. Each row is a membership: one role at
 * exactly one scope (partner / organization / location). The
 * partner-scoped rows map 1:1 to the old user_role_tbl grants; new
 * org/location rows unlock what V0024 adds.
 */
function MembershipsSection(props: { userId: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["user-memberships", props.userId],
    queryFn: () => apiClient.listUserMemberships(props.userId),
  });
  const memberships = q.data?.memberships ?? [];
  const [addOpen, setAddOpen] = useState(false);
  const profile = useRoleProfile();
  // Add-membership is gated by require_write on the target scope path
  // (picked inside the dialog). Hide the add button entirely when the
  // caller has no write grant anywhere. Super-admins always see it.
  const canAdd =
    isSuperAdmin(profile) || (profile?.roles ?? []).some((r) => r.role === "partner_admin");
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["user-memberships", props.userId] });
    void qc.invalidateQueries({ queryKey: ["users"] });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Memberships
        </span>
        {canAdd && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            <Shield className="h-3.5 w-3.5" />
            Add membership
          </button>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading memberships…</p>
      ) : memberships.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          This user has no memberships yet. Add one to grant access at a partner, organization, or
          location.
        </p>
      ) : (
        <ul className="space-y-1.5 text-xs">
          {memberships.map((m) => (
            <MembershipRow key={m.id} userId={props.userId} membership={m} onMutated={invalidate} />
          ))}
        </ul>
      )}

      {addOpen && (
        <AddMembershipForm
          userId={props.userId}
          onClose={() => setAddOpen(false)}
          onMutated={() => {
            setAddOpen(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function MembershipRow(props: {
  userId: string;
  membership: UserMembership;
  onMutated: () => void;
}): JSX.Element {
  const { membership: m } = props;
  const [error, setError] = useState<string | null>(null);
  const [deptsOpen, setDeptsOpen] = useState(false);
  const profile = useRoleProfile();
  // Remove-membership needs require_write on the membership's partner
  // path. Department editor takes the same gate - putMembershipDepartments
  // is behind the same rule. Hide remove / dept-edit buttons entirely
  // when the caller can't mutate this membership.
  const canEdit = hasWriteOn(profile, m.partner_path ?? "");
  const del = useMutation({
    mutationFn: () => apiClient.deleteUserMembership(props.userId, m.id),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const scopeLabel =
    m.scope_kind === "partner"
      ? (m.partner_name ?? m.partner_path ?? "partner")
      : m.scope_kind === "organization"
        ? (m.organization_name ?? "organization")
        : (m.location_name ?? "location");

  return (
    <li className="rounded-md bg-background px-2 py-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span
            className="mr-1 rounded-full bg-muted px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground"
            title={`scope: ${m.scope_kind}`}
          >
            {m.scope_kind}
          </span>
          <span className="font-mono text-primary">{m.role}</span>{" "}
          <span className="text-muted-foreground">on</span>{" "}
          <span className="break-words font-medium">{scopeLabel}</span>
          {canEdit ? (
            <button
              type="button"
              onClick={() => setDeptsOpen((v) => !v)}
              className="ml-2 text-[10px] text-muted-foreground underline hover:text-foreground"
            >
              {m.departments.length === 0
                ? "+ departments"
                : `· ${m.departments.length} dept${m.departments.length === 1 ? "" : "s"}`}
            </button>
          ) : (
            m.departments.length > 0 && (
              <span className="ml-2 text-[10px] text-muted-foreground">
                · {m.departments.length} dept{m.departments.length === 1 ? "" : "s"}
              </span>
            )
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            disabled={del.isPending}
            onClick={() => del.mutate()}
            title="Remove membership"
            className="flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
          >
            <ShieldOff className="h-3 w-3" />
            Remove
          </button>
        )}
      </div>
      {error && <p className="mt-1 text-[11px] text-destructive">{error}</p>}
      {deptsOpen && (
        <MembershipDepartmentsEditor
          userId={props.userId}
          membershipId={m.id}
          membership={m}
          initialSelected={m.departments.map((d) => d.id)}
          onClose={() => setDeptsOpen(false)}
          onSaved={() => {
            setDeptsOpen(false);
            props.onMutated();
          }}
        />
      )}
    </li>
  );
}

/**
 * Per-membership department subset editor. Candidate list is scoped
 * to the membership's tier (V0022 cascade):
 *
 *   * location-scope → ``listLocationDepartments(location_id).effective``
 *     - only the departments active on that specific location, which
 *     may be a curated subset of its vertical's catalog or the raw
 *     vertical catalog when no override is set.
 *   * organization-scope → ``listOrganizationVerticals(org_id)`` -
 *     every department across the verticals the org has enabled.
 *   * partner-scope → the full platform catalog (a partner can span
 *     multiple orgs with different verticals).
 *
 * Before this fix the editor always showed the full catalog, which
 * spilled Cannabis / Retail / Restaurant / Other verticals' depts
 * into an auto-parts location_manager's picker. Empty subset still
 * means "no narrowing" at the current tier.
 */
function MembershipDepartmentsEditor(props: {
  userId: string;
  membershipId: string;
  membership: UserMembership;
  initialSelected: string[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { membership } = props;
  // Loc-scope: fetch effective-at-location depts. Returns {effective, source}.
  const locDepts = useQuery({
    queryKey: ["location-departments", membership.location_id ?? "none"],
    queryFn: () => apiClient.listLocationDepartments(membership.location_id as string),
    enabled: membership.scope_kind === "location" && !!membership.location_id,
    staleTime: 60_000,
  });
  // Org-scope: fetch the org's enabled verticals so we can group.
  const orgVerticals = useQuery({
    queryKey: ["organization-verticals", membership.organization_id ?? "none"],
    queryFn: () => apiClient.listOrganizationVerticals(membership.organization_id as string),
    enabled: membership.scope_kind === "organization" && !!membership.organization_id,
    staleTime: 60_000,
  });
  // Partner-scope: aggregate every vertical that's enabled across the
  // partner's orgs. Mirrors what locations under this partner can
  // actually pick from - instead of dumping the whole platform catalog
  // (Cannabis + Restaurant + Retail + Service + Other) into the
  // picker, only show verticals that real locations under the partner
  // are configured for.
  //
  // Implementation: list orgs under the partner, then for each org
  // pull listOrganizationVerticals (already grouped + departmented).
  // Union by vertical id; union departments per vertical. N+1 calls
  // but each org's verticals query is staleTime-cached so re-opening
  // the picker on a different membership reuses both round-trips.
  const partnerOrgs = useQuery({
    queryKey: ["organizations", "for-membership-deps", membership.partner_id ?? "none"],
    queryFn: () =>
      apiClient.listOrganizations({
        partner_id: membership.partner_id as string,
        include_deleted: false,
        limit: 100,
      }),
    enabled: membership.scope_kind === "partner" && !!membership.partner_id,
    staleTime: 60_000,
  });
  const orgIds = useMemo(
    () => (partnerOrgs.data?.organizations ?? []).map((o) => o.id),
    [partnerOrgs.data],
  );
  const partnerVerticalQueries = useQueries({
    queries: orgIds.map((id) => ({
      queryKey: ["organization-verticals", id],
      queryFn: () => apiClient.listOrganizationVerticals(id),
      staleTime: 60_000,
      enabled: membership.scope_kind === "partner",
    })),
  });
  const partnerVerticalsAggregated = useMemo(() => {
    if (membership.scope_kind !== "partner") return null;
    const byVerticalId = new Map<
      string,
      {
        id: string;
        display_name: string;
        departments: Map<string, { id: string; display_name: string }>;
      }
    >();
    for (const q of partnerVerticalQueries) {
      if (!q.data) continue;
      for (const ov of q.data.verticals) {
        const bucket = byVerticalId.get(ov.vertical_id) ?? {
          id: ov.vertical_id,
          display_name: ov.vertical_display_name,
          departments: new Map(),
        };
        for (const d of ov.departments) {
          bucket.departments.set(d.id, { id: d.id, display_name: d.display_name });
        }
        byVerticalId.set(ov.vertical_id, bucket);
      }
    }
    return Array.from(byVerticalId.values());
  }, [partnerVerticalQueries, membership.scope_kind]);

  const [selected, setSelected] = useState<Set<string>>(new Set(props.initialSelected));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiClient.putMembershipDepartments(props.userId, props.membershipId, Array.from(selected)),
    onSuccess: props.onSaved,
    onError: (err: unknown) => setError(describe(err)),
  });

  // Normalise all three scope branches into the same grouped-by-
  // vertical shape the render path below expects. Location scope
  // returns a flat list already merged by V0022; synthesise a single
  // "pseudo-vertical" so the grouping ceremony still works.
  interface VerticalGroup {
    id: string;
    display_name: string;
    departments: { id: string; display_name: string }[];
  }
  let verticalGroups: VerticalGroup[] = [];
  let isLoading = false;
  if (membership.scope_kind === "location") {
    isLoading = locDepts.isLoading;
    if (locDepts.data) {
      verticalGroups = [
        {
          id: "__location__",
          display_name: membership.location_name ?? "Location",
          departments: locDepts.data.effective.map((d) => ({
            id: d.id,
            display_name: d.display_name,
          })),
        },
      ];
    }
  } else if (membership.scope_kind === "organization") {
    isLoading = orgVerticals.isLoading;
    if (orgVerticals.data) {
      verticalGroups = orgVerticals.data.verticals.map((ov) => ({
        id: ov.id,
        display_name: ov.vertical_display_name,
        departments: ov.departments.map((d) => ({
          id: d.id,
          display_name: d.display_name,
        })),
      }));
    }
  } else {
    // Partner scope: show union of orgs' enabled verticals, grouped.
    isLoading = partnerOrgs.isLoading || partnerVerticalQueries.some((q) => q.isLoading);
    if (partnerVerticalsAggregated) {
      verticalGroups = partnerVerticalsAggregated.map((v) => ({
        id: v.id,
        display_name: v.display_name,
        departments: Array.from(v.departments.values()),
      }));
    }
  }
  const total = verticalGroups.reduce((acc, v) => acc + v.departments.length, 0);

  // "Empty selected" already means inherit-all (no narrowing) on the
  // server; reflect that in the UI by showing every visible dept as
  // checked when the user hasn't explicitly narrowed yet. The first
  // unchecked box flips selected from {} to a real subset (everything
  // EXCEPT the unchecked dept). Avoids the Sales/Service/Bar/Returns
  // confusion the catalog dump produced.
  const userHasNarrowed = props.initialSelected.length > 0;
  const isVisuallyChecked = (deptId: string): boolean => {
    if (selected.has(deptId)) return true;
    if (!userHasNarrowed && selected.size === 0) return true;
    return false;
  };
  const seedAllVisibleAndToggle = (deptId: string, check: boolean): void => {
    if (!userHasNarrowed && selected.size === 0) {
      // First click: materialise "all visible" then apply the toggle.
      const all = new Set(verticalGroups.flatMap((v) => v.departments.map((d) => d.id)));
      if (check) all.add(deptId);
      else all.delete(deptId);
      setSelected(all);
      return;
    }
    const next = new Set(selected);
    if (check) next.add(deptId);
    else next.delete(deptId);
    setSelected(next);
  };

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/30 p-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="font-medium text-muted-foreground">
          Departments ({selected.size}
          {total > 0 ? ` / ${total}` : ""} selected)
        </span>
        <button
          type="button"
          onClick={props.onClose}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      {isLoading ? (
        <p className="text-muted-foreground">Loading departments…</p>
      ) : verticalGroups.length === 0 ? (
        <p className="text-muted-foreground">No departments available at this scope.</p>
      ) : (
        <div className="space-y-2">
          {verticalGroups.map((v) => (
            <div key={v.id}>
              <div className="mb-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                {v.display_name}
              </div>
              {v.departments.length === 0 ? (
                <p className="text-[11px] italic text-muted-foreground">
                  No departments defined on this vertical.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {v.departments.map((d) => (
                    <label key={d.id} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={isVisuallyChecked(d.id)}
                        onChange={(e) => seedAllVisibleAndToggle(d.id, e.target.checked)}
                        className="h-3 w-3 accent-primary"
                      />
                      <span>{d.display_name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-destructive">{error}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setSelected(new Set())}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Clear all (inherit)
        </button>
      </div>
    </div>
  );
}

// Local UI scope_kind - adds a "platform" pseudo-option on top of the
// three backend scope_kinds. "Platform" represents a super_admin grant
// on the platform partner row; the form auto-resolves it to
// ``{scope_kind: "partner", scope_id: <platform.id>, role: "super_admin"}``
// when submitting so the operator never has to think of super_admin as
// a partner-scope thing.
type UiScopeKind = UserMembershipScopeKind | "platform";

function AddMembershipForm(props: {
  userId: string;
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const [scopeKind, setScopeKind] = useState<UiScopeKind>("partner");
  const [scopeId, setScopeId] = useState("");
  const [role, setRole] = useState<UserRole>("partner_viewer");
  const [error, setError] = useState<string | null>(null);
  const profile = useRoleProfile();
  const callerIsSuper = isSuperAdmin(profile);

  // Resolve the platform partner row via a dedicated query. Earlier
  // versions reused the sidebar's ``listPartners({ limit: 100 })``
  // query, which (a) attached a second observer to the same key -
  // extra rerenders for no benefit - and (b) silently breaks when a
  // super_admin can see more than 100 partners and the platform row
  // doesn't land on the first page. ``partner_type=platform`` returns
  // exactly one row in any sane deployment.
  const platformPartnerQuery = useQuery({
    queryKey: ["partners", "platform-row", profile?.primary_partner?.id ?? null],
    queryFn: () => apiClient.listPartners({ partner_type: "platform", limit: 1 }),
    staleTime: 5 * 60_000,
    // Only fire it when the operator actually needs it - partner_admin
    // callers never see the Platform radio, and even super_admins
    // don't need the row until they pick that scope.
    enabled: !!profile && callerIsSuper && scopeKind === "platform",
  });
  const platformPartner = platformPartnerQuery.data?.partners?.[0];

  // Available roles per scope_kind. ``super_admin`` is exposed ONLY
  // under the new "Platform" scope (and only to super callers) - no
  // longer offered at partner scope, because operators kept reading
  // "super_admin under partner X" as if super was per-partner.
  const availableRoles: ReadonlyArray<UserRole> =
    scopeKind === "platform"
      ? ["super_admin"]
      : rolesForScope(scopeKind).filter((r) => r !== "super_admin");
  if (!availableRoles.includes(role)) {
    const fallback = availableRoles[0];
    if (fallback && fallback !== role) {
      // Defer the setState to avoid React "set state during render"
      // warnings - queue via microtask.
      queueMicrotask(() => setRole(fallback));
    }
  }

  // Scope picker data - orgs + locations come from the caller's scope.
  // Partner picker already filters by caller's subtree.
  const orgsQuery = useQuery({
    queryKey: ["organizations", "for-membership"],
    queryFn: () => apiClient.listOrganizations(),
    enabled: scopeKind === "organization",
  });
  const locationsQuery = useQuery({
    queryKey: ["locations", "for-membership"],
    queryFn: () => apiClient.listLocations({}),
    enabled: scopeKind === "location",
  });

  const create = useMutation({
    mutationFn: () => {
      // Translate the UI's "platform" pseudo-scope into the wire
      // shape the backend accepts: a partner-scope grant pinned to
      // the platform partner row, role super_admin.
      if (scopeKind === "platform") {
        if (!platformPartner) {
          throw new Error("platform partner not found in caller's scope");
        }
        return apiClient.createUserMembership(props.userId, {
          scope_kind: "partner",
          scope_id: platformPartner.id,
          role: "super_admin",
        });
      }
      return apiClient.createUserMembership(props.userId, {
        scope_kind: scopeKind,
        scope_id: scopeId,
        role,
      });
    },
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
      className="mt-3 space-y-2 rounded-md border bg-background p-3"
    >
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="font-medium text-muted-foreground">Scope</span>
        {/* Platform scope is offered only to super_admin callers and
            represents the platform-wide super_admin grant. The other
            three scope kinds are the per-resource membership tiers. */}
        {(callerIsSuper
          ? (["platform", "partner", "organization", "location"] as const)
          : (["partner", "organization", "location"] as const)
        ).map((k) => (
          <label key={k} className="flex items-center gap-1">
            <input
              type="radio"
              name={`scope-kind-${props.userId}`}
              checked={scopeKind === k}
              onChange={() => {
                setScopeKind(k);
                setScopeId("");
              }}
              className="h-3 w-3 accent-primary"
            />
            <span className="capitalize">{k}</span>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
        {scopeKind === "platform" ? (
          // No picker needed - there's exactly one platform partner
          // row in the system, auto-resolved on submit. Surface the
          // resolution state so the operator knows the grant is
          // pinned to a real row before they hit Add (and gets a
          // clear error if the lookup fails).
          <div
            className={cn(
              "rounded-md border border-dashed px-2 py-1 text-xs",
              platformPartnerQuery.isError
                ? "border-destructive bg-destructive/10 text-destructive"
                : "bg-muted/30 text-muted-foreground",
            )}
          >
            {platformPartnerQuery.isLoading
              ? "Loading platform partner…"
              : platformPartnerQuery.isError
                ? `Failed to resolve platform partner: ${describe(platformPartnerQuery.error)}`
                : platformPartner
                  ? `Platform-wide super_admin grant - ${platformPartner.name}`
                  : "Platform partner row not found in your scope."}
          </div>
        ) : scopeKind === "partner" ? (
          <PartnerPicker required value={scopeId} onChange={setScopeId} className="py-1 text-xs" />
        ) : scopeKind === "organization" ? (
          <select
            required
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
            className="rounded-md border bg-background px-2 py-1 text-xs"
          >
            <option value="">Select organization…</option>
            {(orgsQuery.data?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <select
            required
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
            className="rounded-md border bg-background px-2 py-1 text-xs"
          >
            <option value="">Select location…</option>
            {(locationsQuery.data?.locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className="rounded-md border bg-background px-2 py-1 text-xs"
        >
          {availableRoles.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </select>
        <div className="flex gap-1">
          <button
            type="submit"
            disabled={
              create.isPending ||
              // For platform scope the scope_id is auto-resolved on
              // submit, so we just need the partners list to have
              // loaded the platform row. For the other scopes we
              // require the user to pick something.
              (scopeKind === "platform" ? !platformPartner : !scopeId)
            }
            className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Add
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </form>
  );
}

/** Roles valid at each scope_kind. Mirrors backend ``_validate_role_scope``. */
const ROLES_BY_SCOPE: Record<"partner" | "organization" | "location", UserRole[]> = {
  partner: ["partner_admin", "partner_viewer"],
  organization: ["organization_admin"],
  location: ["location_manager"],
};

export interface InvitePresetScope {
  scope_kind: "partner" | "organization" | "location";
  scope_id: string;
  partner_id: string;
  partner_path: string;
  /** Display name shown in the dialog header - e.g. "Honda Group"
   *  or "Frank Leta Honda". */
  label: string;
}

export function InviteDialog(props: {
  onClose: () => void;
  onMutated: () => void;
  /** When set, the dialog forces the new user's scope to this preset
   *  and shows a role-only picker. Used by Org Detail / Location
   *  Detail "Invite to <name>" buttons. */
  presetScope?: InvitePresetScope;
}): JSX.Element {
  const { scope } = usePartnerScope();
  const profile = useRoleProfile();
  // Caller's effective grants - the scope picker (no preset path)
  // lists each grant the caller holds so a partner_admin can pick
  // "their partner", an org_admin "their org", a location_manager
  // "their location". Super_admin ignores this and uses the partner
  // picker.
  const isSuper = isSuperAdmin(profile);
  // Highest tier the caller can grant at. Super = all 3 tiers via
  // its short-circuit; otherwise derive from effective grants.
  const callerTier = useMemo(() => {
    if (isSuper) return 3;
    const rank = { partner: 3, organization: 2, location: 1 } as const;
    let max = 0;
    for (const r of profile?.roles ?? []) {
      const k = (r.scope_kind ?? "partner") as "partner" | "organization" | "location";
      if (rank[k] > max) max = rank[k];
    }
    return max;
  }, [isSuper, profile]);

  // Cascading scope picker: scope_kind → partner → (org if applicable)
  // → (location if applicable). Each level loads on demand and is
  // bounded by the parent pick so a 1000-org partner doesn't dump the
  // entire list into a single dropdown. Backend require_write_*
  // backstops anything misclicked.
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const initialScopeKind: "partner" | "organization" | "location" =
    callerTier >= 3 ? "partner" : callerTier >= 2 ? "organization" : "location";
  const [scopeKind, setScopeKind] = useState<"partner" | "organization" | "location">(
    initialScopeKind,
  );
  const [scopePartnerId, setScopePartnerId] = useState<string>(scope.id);
  const [scopeOrgId, setScopeOrgId] = useState<string>("");
  const [scopeLocId, setScopeLocId] = useState<string>("");

  const orgsQuery = useQuery({
    queryKey: ["organizations", "invite-cascade", scopePartnerId],
    queryFn: () =>
      apiClient.listOrganizations({
        partner_id: scopePartnerId,
        limit: 200,
      }),
    staleTime: 60_000,
    enabled: !!scopePartnerId && scopeKind !== "partner",
  });
  const locsQuery = useQuery({
    queryKey: ["locations", "invite-cascade", scopeOrgId],
    queryFn: () =>
      apiClient.listLocations({
        organization_id: scopeOrgId,
        limit: 200,
      }),
    staleTime: 60_000,
    enabled: !!scopeOrgId && scopeKind === "location",
  });

  // Partner row for the picked partner - needed to surface
  // partner_path on the submit payload + to label the dialog.
  const partnerLookupQuery = useQuery({
    queryKey: ["partners", "invite-cascade-lookup", scopePartnerId],
    queryFn: () => apiClient.getPartner(scopePartnerId),
    enabled: !!scopePartnerId,
    staleTime: 60_000,
  });

  // Reset cascading children when parent changes.
  useEffect(() => {
    setScopeOrgId("");
    setScopeLocId("");
  }, [scopePartnerId, scopeKind]);
  useEffect(() => {
    setScopeLocId("");
  }, [scopeOrgId]);

  const grantRoleOptions = ROLES_BY_SCOPE[scopeKind];
  const [grantRole, setGrantRole] = useState<UserRole | "">(
    scopeKind === "partner" ? "" : grantRoleOptions[0],
  );
  // Re-anchor role when scope_kind changes. Partner allows "no role";
  // org/loc force the first valid role (granting is the point).
  useEffect(() => {
    setGrantRole(scopeKind === "partner" ? "" : ROLES_BY_SCOPE[scopeKind][0]);
  }, [scopeKind]);

  const presetRoleOptions = props.presetScope ? ROLES_BY_SCOPE[props.presetScope.scope_kind] : [];
  const [presetRole, setPresetRole] = useState<UserRole>(
    presetRoleOptions[0] ?? "location_manager",
  );

  // Resolved scope_id for the submit payload.
  const resolvedScopeId =
    scopeKind === "partner"
      ? scopePartnerId
      : scopeKind === "organization"
        ? scopeOrgId
        : scopeLocId;
  const [error, setError] = useState<string | null>(null);
  const emailCheck = validateEmail(email, { required: true });

  const invite = useMutation({
    mutationFn: () => {
      if (!emailCheck.ok) throw new Error(emailCheck.error);
      const base = {
        email: email.trim(),
        ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
        ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
      };
      // Two branches:
      //   1. presetScope (Org/Loc detail Invite button) - lock scope,
      //      role-only picker; works for any caller tier.
      //   2. Universal scope tree - every caller (super included)
      //      picks a partner / org / location scope from the tree
      //      and a valid role for that scope_kind. Partner scope
      //      additionally accepts "no role" so a user can be created
      //      under a partner with the role attached later.
      if (props.presetScope) {
        return apiClient.inviteUser({
          ...base,
          primary_partner_id: props.presetScope.partner_id,
          initial_role: {
            scope_kind: props.presetScope.scope_kind,
            scope_id: props.presetScope.scope_id,
            role: presetRole,
          },
        });
      }
      if (!scopePartnerId || !resolvedScopeId) {
        throw new Error("Pick a scope before sending the invite.");
      }
      return apiClient.inviteUser({
        ...base,
        primary_partner_id: scopePartnerId,
        // Send initial_role only when a role is picked. Empty role
        // (partner-scope only) means "create the user, no membership
        // yet" - admin grants from the user's row later.
        ...(grantRole
          ? {
              initial_role: {
                scope_kind: scopeKind,
                scope_id: resolvedScopeId,
                role: grantRole,
              },
            }
          : {}),
      });
    },
    onSuccess: () => {
      setError(null);
      props.onMutated();
      props.onClose();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const submitDisabled =
    invite.isPending || !emailCheck.ok || (props.presetScope ? false : !resolvedScopeId);

  return (
    <ModalOverlay ariaLabel="Invite user" onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
        className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-4 shadow-lg sm:rounded-xl sm:p-5"
      >
        <h2 className="text-sm font-semibold">Invite user</h2>

        <div>
          <label
            htmlFor="inv-email"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Email
          </label>
          <input
            id="inv-email"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!emailCheck.ok}
            className={cn(
              "w-full rounded-md border bg-background px-3 py-2 text-sm",
              !emailCheck.ok && "border-destructive",
            )}
          />
          {!emailCheck.ok && (
            <p className="mt-1 text-[11px] text-destructive">{emailCheck.error}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="inv-fname"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              First name
            </label>
            <input
              id="inv-fname"
              type="text"
              autoComplete="off"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="inv-lname"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Last name
            </label>
            <input
              id="inv-lname"
              type="text"
              autoComplete="off"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>

        {props.presetScope ? (
          <div className="space-y-2">
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
              <div className="text-muted-foreground">Adding to</div>
              <div className="mt-0.5 font-medium">{props.presetScope.label}</div>
              <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                {props.presetScope.scope_kind} · {props.presetScope.partner_path}
              </div>
            </div>
            <div>
              <label
                htmlFor="inv-role"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Role
              </label>
              <select
                id="inv-role"
                required
                value={presetRole}
                onChange={(e) => setPresetRole(e.target.value as UserRole)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {presetRoleOptions.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label
                htmlFor="inv-scope-kind"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Scope tier
              </label>
              <select
                id="inv-scope-kind"
                value={scopeKind}
                onChange={(e) =>
                  setScopeKind(e.target.value as "partner" | "organization" | "location")
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {callerTier >= 3 && <option value="partner">Partner</option>}
                {callerTier >= 2 && <option value="organization">Organization</option>}
                {callerTier >= 1 && <option value="location">Location</option>}
              </select>
            </div>
            <div>
              <label
                htmlFor="inv-partner"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Partner
              </label>
              <PartnerPicker
                id="inv-partner"
                required
                value={scopePartnerId}
                onChange={setScopePartnerId}
              />
            </div>
            {scopeKind !== "partner" && (
              <div>
                <label
                  htmlFor="inv-org"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Organization
                </label>
                <select
                  id="inv-org"
                  required
                  value={scopeOrgId}
                  onChange={(e) => setScopeOrgId(e.target.value)}
                  disabled={!scopePartnerId || orgsQuery.isLoading}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
                >
                  <option value="">
                    {!scopePartnerId
                      ? "Select a partner first"
                      : orgsQuery.isLoading
                        ? "Loading…"
                        : (orgsQuery.data?.organizations ?? []).length === 0
                          ? "No organizations under this partner"
                          : "Select an organization…"}
                  </option>
                  {(orgsQuery.data?.organizations ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {scopeKind === "location" && (
              <div>
                <label
                  htmlFor="inv-loc"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Location
                </label>
                <select
                  id="inv-loc"
                  required
                  value={scopeLocId}
                  onChange={(e) => setScopeLocId(e.target.value)}
                  disabled={!scopeOrgId || locsQuery.isLoading}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
                >
                  <option value="">
                    {!scopePartnerId
                      ? "Select a partner first"
                      : !scopeOrgId
                        ? "Select an organization first"
                        : locsQuery.isLoading
                          ? "Loading…"
                          : (locsQuery.data?.locations ?? []).length === 0
                            ? "No locations under this organization"
                            : "Select a location…"}
                  </option>
                  {(locsQuery.data?.locations ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label
                htmlFor="inv-grant-role"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Role
                {scopeKind === "partner" && (
                  <span className="text-muted-foreground/80"> (optional)</span>
                )}
              </label>
              <select
                id="inv-grant-role"
                required={scopeKind !== "partner"}
                value={grantRole}
                onChange={(e) => setGrantRole(e.target.value as UserRole | "")}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {scopeKind === "partner" && <option value="">No role yet - grant later</option>}
                {grantRoleOptions.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
              {partnerLookupQuery.data && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Lands under {partnerLookupQuery.data.name} ({partnerLookupQuery.data.path}).
                </p>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitDisabled}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" />
            {invite.isPending ? "Sending invite…" : "Send invite"}
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}
